/* AI Price Tracker: pure math, parsing, formatting, filtering. No DOM. Unit-tested in tests/model.test.js. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.AIPT_MODEL = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';
  var TOK_MIN_LOG = -1, TOK_MAX_LOG = 4, POS_MAX = 1000; // slider spans 0.1M to 10,000M tokens per month

  function n(v, d) { v = +v; return isFinite(v) ? v : (d === undefined ? 0 : d); }
  function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

  // API cost for one month. tokensM = millions of tokens per month. inShare and cacheShare are 0..1.
  // Cached input is billed at the cache-read price when the file lists one, otherwise at the input price.
  function apiCost(o) {
    var t = Math.max(0, n(o.tokensM)), inS = clamp(n(o.inShare, 0.75), 0, 1), cS = clamp(n(o.cacheShare, 0), 0, 1);
    var p = o.price || {}, pin = n(p.in), pout = n(p.out);
    var pcr = (p.cacheRead === null || p.cacheRead === undefined || !isFinite(+p.cacheRead)) ? pin : +p.cacheRead;
    var inTok = t * inS, outTok = t - inTok;
    var inCost = inTok * (1 - cS) * pin + inTok * cS * pcr, outCost = outTok * pout;
    return { inTokM: inTok, outTokM: outTok, inCost: inCost, outCost: outCost, total: inCost + outCost };
  }
  // Team cost: seats x plan price + monthly API usage.
  function teamCost(o) {
    var seats = Math.max(0, Math.round(n(o.seats))), plan = Math.max(0, n(o.planPrice));
    var api = apiCost(o), seatTotal = seats * plan, total = seatTotal + api.total;
    return { seats: seats, planPrice: plan, seatTotal: seatTotal, api: api.total, apiDetail: api, total: total, annual: total * 12,
      perSeat: seats > 0 ? total / seats : null, seatShare: total > 0 ? seatTotal / total : 0, apiShare: total > 0 ? api.total / total : 0 };
  }
  function blended(i, o) { return (3 * n(i) + n(o)) / 4; }
  function median(a) {
    var x = a.filter(function (v) { return typeof v === 'number' && isFinite(v); }).sort(function (p, q) { return p - q; });
    if (!x.length) return null; var m = x.length >> 1; return x.length % 2 ? x[m] : (x[m - 1] + x[m]) / 2;
  }
  // Log slider mapping for tokens.
  function posToTokens(pos) {
    pos = clamp(Math.round(n(pos)), 0, POS_MAX); if (pos === 0) return 0;
    var v = Math.pow(10, TOK_MIN_LOG + (pos / POS_MAX) * (TOK_MAX_LOG - TOK_MIN_LOG));
    return niceRound(v);
  }
  function tokensToPos(t) {
    t = n(t); if (t <= 0) return 0;
    var p = (Math.log10(t) - TOK_MIN_LOG) / (TOK_MAX_LOG - TOK_MIN_LOG) * POS_MAX;
    return clamp(Math.round(p), 1, POS_MAX);
  }
  function niceRound(v) {
    if (v >= 1000) return Math.round(v / 10) * 10;
    if (v >= 100) return Math.round(v);
    if (v >= 10) return Math.round(v * 2) / 2;
    if (v >= 1) return Math.round(v * 10) / 10;
    return Math.round(v * 100) / 100;
  }
  // Parse typed values like "50M", "1.2B", "250k", "$30", "75%", "1,500".
  function parseNum(s, kind) {
    if (typeof s === 'number') return isFinite(s) ? s : null;
    var t = String(s == null ? '' : s).trim().toLowerCase().replace(/[$,\s]/g, '').replace(/usd$/, '');
    if (!t) return null;
    var pct = /%$/.test(t); t = t.replace(/%$/, '');
    var unit = (t.match(/(k|m|b|bn|t)$/) || [''])[0]; t = t.replace(/(k|m|b|bn|t)$/, '');
    t = t.replace(/(tokens?|seats?|users?|\/mo|\/month)$/, '');
    if (!/^-?\d*\.?\d+$/.test(t)) return null;
    var v = parseFloat(t);
    var mult = { '': 1, k: 1e3, m: 1e6, b: 1e9, bn: 1e9, t: 1e12 }[unit];
    if (kind === 'tokensM') { v = unit ? v * mult / 1e6 : v; }
    else if (kind === 'pct') { v = v; }
    else v = v * mult;
    if (pct && kind !== 'pct') v = v / 100;
    return isFinite(v) ? v : null;
  }
  // Formatting (no em or en dashes anywhere).
  function fmtUSD(v, opt) {
    opt = opt || {}; if (v === null || v === undefined || !isFinite(v)) return 'n/a';
    var a = Math.abs(v), s = v < 0 ? '-' : '';
    if (opt.compact && a >= 1e6) return s + '$' + (a / 1e6).toFixed(a >= 1e7 ? 1 : 2).replace(/\.0+$/, '') + 'M';
    if (opt.compact && a >= 1e4) return s + '$' + (a / 1e3).toFixed(a >= 1e5 ? 0 : 1).replace(/\.0$/, '') + 'K';
    var dp = (a >= 100 || a === 0) ? 0 : 2;
    return s + '$' + a.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
  }
  function fmtPerM(v) {
    if (v === null || v === undefined || !isFinite(v)) return 'n/a';
    if (v === 0) return '$0';
    var a = Math.abs(v), dp = a >= 100 ? 0 : a >= 10 ? 2 : a >= 1 ? 2 : a >= 0.1 ? 3 : 4;
    var s = a.toFixed(dp); if (dp > 2) s = s.replace(/0+$/, '').replace(/\.$/, ''); if (/\.\d$/.test(s)) s += '0';
    return '$' + s;
  }
  function fmtTokens(v) {
    if (v === null || v === undefined || !isFinite(v) || v <= 0) return 'n/a';
    if (v >= 1e6) return +(v / 1048576 >= 0.99 && v % 1048576 === 0 ? v / 1048576 : v / 1e6).toFixed(2) + 'M';
    if (v >= 1000) return Math.round(v / 1000) + 'K';
    return String(v);
  }
  function fmtTokM(m) {
    if (!isFinite(m) || m <= 0) return '0';
    if (m >= 1000) return +(m / 1000).toFixed(2) + 'B';
    return +m.toFixed(m >= 10 ? 1 : 2) + 'M';
  }
  function fmtPct(p, dp) { if (p === null || p === undefined || !isFinite(p)) return 'n/a'; var s = (p > 0 ? '+' : p < 0 ? '-' : '') + Math.abs(p).toFixed(dp === undefined ? 1 : dp) + '%'; return s; }
  function displayName(key, provider) { var k = String(key); var pre = provider + '/'; return k.indexOf(pre) === 0 ? k.slice(pre.length) : k; }
  // Rows are arrays per payload.cols. Build objects once.
  function toObjects(payload) {
    var c = payload.cols, ix = {}; c.forEach(function (k, i) { ix[k] = i; });
    return payload.rows.map(function (r) {
      return { key: r[ix.key], provider: r[ix.provider], name: displayName(r[ix.key], r[ix.provider]), in: r[ix['in']], out: r[ix.out], cacheRead: r[ix.cacheRead],
        cacheWrite: r[ix.cacheWrite], ctx: r[ix.ctx], maxOut: r[ix.maxOut], src: r[ix.src] >= 0 ? payload.sources[r[ix.src]] : null, fp: r[ix.firstParty] === 1,
        chg7: r[ix.chg7], firstSeen: r[ix.firstSeen], blended: blended(r[ix['in']], r[ix.out]) };
    });
  }
  function filterRows(rows, f) {
    f = f || {}; var q = String(f.q || '').trim().toLowerCase(), terms = q ? q.split(/\s+/) : [];
    return rows.filter(function (r) {
      if (f.scope === 'fp' && !r.fp) return false;
      if (f.scope === 'changed' && (r.chg7 === null || r.chg7 === undefined)) return false;
      if (f.provider && f.provider !== 'all' && r.provider !== f.provider) return false;
      if (terms.length) { var h = (r.key + ' ' + r.provider + ' ' + (f.labels && f.labels[r.provider] || '')).toLowerCase(); for (var i = 0; i < terms.length; i++) if (h.indexOf(terms[i]) < 0) return false; }
      return true;
    });
  }
  function sortRows(rows, key, dir) {
    var d = dir === 'desc' ? -1 : 1;
    return rows.slice().sort(function (a, b) {
      var x = a[key], y = b[key];
      var xn = x === null || x === undefined, yn = y === null || y === undefined;
      if (xn && yn) return a.key < b.key ? -1 : 1; if (xn) return 1; if (yn) return -1; // nulls last in both directions
      // provSort ties break by larger context window, then model key descending (newer models first), other text columns A to Z.
      if (typeof x === 'string') { var c = x.localeCompare(y); if (c) return c * d; if (key === 'provSort') { var cx = (b.ctx || 0) - (a.ctx || 0); if (cx) return cx; return a.key < b.key ? 1 : -1; } return a.key < b.key ? -1 : 1; }
      if (x !== y) return (x - y) * d;
      if (a.provider !== b.provider) return a.provider < b.provider ? -1 : 1;
      return a.key < b.key ? -1 : 1;
    });
  }
  return { apiCost: apiCost, teamCost: teamCost, blended: blended, median: median, posToTokens: posToTokens, tokensToPos: tokensToPos, niceRound: niceRound,
    parseNum: parseNum, fmtUSD: fmtUSD, fmtPerM: fmtPerM, fmtTokens: fmtTokens, fmtTokM: fmtTokM, fmtPct: fmtPct, displayName: displayName,
    toObjects: toObjects, filterRows: filterRows, sortRows: sortRows, clamp: clamp, POS_MAX: POS_MAX };
});
